import { useEffect, useState } from 'react';
import { jwtDecode, type JwtPayload } from 'jwt-decode';
import queryString from 'query-string';
import {
  AuthStorageKeys,
  HTTP_STATUS_UNAUTHORIZED,
  MIDWAY_AUTH_BASE_URL,
  MIDWAY_AUTH_HOSTNAME,
  MIDWAY_DEFAULT_PORT,
  MIDWAY_HTTPS_PROTOCOL,
  MIDWAY_ID_TOKEN_PARAM,
  MIDWAY_NONCE_WORD_COUNT,
  MIDWAY_REDIRECT_PATH,
  MIDWAY_REDIRECT_URI_PARAM,
  MIDWAY_RESPONSE_TYPE_ID_TOKEN,
  MIDWAY_SCOPE_OPENID,
  MIDWAY_SENTRY_HANDLER_VERSION,
  MIDWAY_URL_CLEANUP_DELAY_MS,
} from './constants';

interface MidwayRequestParams {
  scope: string;
  response_type: string;
  client_id: string;
  redirect_uri: string;
  nonce: string;
  sentry_handler_version?: string;
}

export interface UseMidwayResult {
  /** True while the initial token round-trip is in flight. */
  isLoading: boolean;
  /** True if the auth handshake produced an unrecoverable error. */
  isError: boolean;
  /** The Error instance if isError, otherwise null. */
  error: Error | null;
  /** Logged-in user's alias, empty string until the token resolves. */
  userAlias: string;
}

const generateNonce = (): string =>
  crypto
    .getRandomValues(new Uint32Array(MIDWAY_NONCE_WORD_COUNT))
    .toString()
    .replace(/,/g, '');

const buildMidwayRequestUrl = (shouldRedirect: boolean): string => {
  const port = window.location.port || MIDWAY_DEFAULT_PORT;
  const params: MidwayRequestParams = {
    scope: MIDWAY_SCOPE_OPENID,
    response_type: MIDWAY_RESPONSE_TYPE_ID_TOKEN,
    client_id: `${MIDWAY_HTTPS_PROTOCOL}//${window.location.hostname}:${port}`,
    redirect_uri: `${window.location.protocol}//${window.location.hostname}:${port}/`,
    nonce: generateNonce(),
  };
  if (!shouldRedirect) {
    params.sentry_handler_version = MIDWAY_SENTRY_HANDLER_VERSION;
  }
  const baseUrl = shouldRedirect
    ? `${MIDWAY_AUTH_BASE_URL}${MIDWAY_REDIRECT_PATH}?`
    : `${MIDWAY_AUTH_BASE_URL}?`;
  return baseUrl + queryString.stringify(params);
};

/**
 * Fetch a Midway token without redirection.
 * If the response is 401, redirects to Midway for interactive sign-in.
 */
const fetchMidwayToken = async (): Promise<string | undefined> => {
  const response = await fetch(buildMidwayRequestUrl(false), {
    credentials: 'include',
  });
  if (!response.ok) {
    if (response.status === HTTP_STATUS_UNAUTHORIZED) {
      // eslint-disable-next-line no-restricted-syntax -- intentional full-page redirect to Midway auth host
      window.location.href = buildMidwayRequestUrl(true);
      return undefined;
    }
    throw new Error(`Midway request failed: ${response.status}`);
  }
  return response.text();
};

/**
 * Strip the id_token from the URL after we've consumed it.
 */
const cleanupUrl = (): void => {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(MIDWAY_ID_TOKEN_PARAM)) return;
  url.searchParams.delete(MIDWAY_ID_TOKEN_PARAM);
  window.history.replaceState({}, document.title, url.toString());
};

/**
 * If we landed on Midway's own host with a redirect+token pair, bounce back
 * to the original app URL with the token attached. Returns true if a redirect
 * was performed, in which case the caller should stop initialization.
 */
const handleMidwayRedirectHost = (): boolean => {
  if (window.location.hostname !== MIDWAY_AUTH_HOSTNAME) return false;
  const urlParams = new URLSearchParams(window.location.search);
  const redirectUri = urlParams.get(MIDWAY_REDIRECT_URI_PARAM);
  const token = urlParams.get(MIDWAY_ID_TOKEN_PARAM);
  if (redirectUri && token) {
    // eslint-disable-next-line no-restricted-syntax -- intentional full-page redirect back to app with token
    window.location.href = `${redirectUri}?${MIDWAY_ID_TOKEN_PARAM}=${token}`;
    return true;
  }
  return false;
};

/**
 * Decodes the token, persists the alias for synchronous readers, and
 * returns the alias.
 *
 * Legacy reads this key through @uidotdev/usehooks' useLocalStorage,
 * which JSON.parses on read. Match that contract so legacy doesn't
 * crash trying to parse a raw string while both apps coexist.
 */
const decodeAndStoreAlias = (token: string): string => {
  const decoded: JwtPayload = jwtDecode(token);
  const alias = decoded.sub ?? '';
  window.localStorage.setItem(AuthStorageKeys.MidwayAuthUsername, JSON.stringify(alias));
  return alias;
};

/**
 * Runs the full token acquisition flow. Resolves with the user alias, or
 * null when a redirect is in flight (Midway host bounce or interactive
 * sign-in) and this page load will not complete authentication.
 */
const acquireMidwayAlias = async (): Promise<string | null> => {
  if (handleMidwayRedirectHost()) {
    // Redirect is in flight — this page is unloading. Never resolve, so the
    // caller's .then/.finally never run and isLoading stays true until the
    // navigation completes. Resolving here would flip isLoading to false
    // with an empty alias and no error, flashing unauthenticated UI.
    // (Matches the pre-refactor behavior, where the early return skipped
    // the try/finally entirely.)
    return new Promise<never>(() => undefined);
  }

  const urlParams = new URLSearchParams(window.location.search);
  const tokenFromUrl = urlParams.get(MIDWAY_ID_TOKEN_PARAM);
  if (tokenFromUrl) {
    const alias = decodeAndStoreAlias(tokenFromUrl);
    // Delay cleanup so any other listener can read the token first.
    window.setTimeout(cleanupUrl, MIDWAY_URL_CLEANUP_DELAY_MS);
    return alias;
  }

  const token = await fetchMidwayToken();
  return token ? decodeAndStoreAlias(token) : null;
};

/**
 * v2 Midway hook. Mirrors the legacy auth flow but is owned by v2:
 *   - No Redux
 *   - No imports from src/hooks or src/data
 *   - Only side effect outside React state is updating localStorage with the
 *     decoded alias so the rest of the app can read it synchronously.
 */
export function useMidway(): UseMidwayResult {
  const [userAlias, setUserAlias] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    acquireMidwayAlias()
      .then((alias) => {
        if (!isMounted || alias === null) return;
        setUserAlias(alias);
        setIsError(false);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!isMounted) return;
        setIsError(true);
        setError(err instanceof Error ? err : new Error('Failed to initialize Midway'));
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return { userAlias, isLoading, isError, error };
}
